"use client";

/**
 * PreferencesContext — LOCAL device state only (never sent to the Server).
 * Theme, dyslexia font, haptics/audio and notification toggles. Persisted to
 * localStorage and reflected onto <html> data attributes for CSS theming.
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { DEFAULT_PREFERENCES, type UIPreferences } from "@/types/local";
import { resolveThemeAttribute } from "@/styles/theme";

const STORAGE_KEY = "ostudent.preferences.v1";

interface PreferencesContextValue {
  preferences: UIPreferences;
  updatePreferences: (patch: Partial<UIPreferences>) => void;
}

const PreferencesContext = createContext<PreferencesContextValue | null>(null);

function applyToDocument(preferences: UIPreferences) {
  const systemPrefersDark = window.matchMedia(
    "(prefers-color-scheme: dark)",
  ).matches;
  const root = document.documentElement;
  root.setAttribute(
    "data-theme",
    resolveThemeAttribute(preferences.theme.mode, systemPrefersDark),
  );
  root.setAttribute("data-accent", preferences.theme.accent);
  root.setAttribute("data-dyslexia", String(preferences.dyslexiaFont));
}

export function PreferencesProvider({ children }: { children: React.ReactNode }) {
  const [preferences, setPreferences] = useState<UIPreferences>(DEFAULT_PREFERENCES);

  // Hydration-safe localStorage load: the server always renders defaults, so
  // stored preferences must be applied after mount (one intentional re-render).
  useEffect(() => {
    try {
      const raw = window.localStorage.getItem(STORAGE_KEY);
      if (raw) {
        // eslint-disable-next-line react-hooks/set-state-in-effect
        setPreferences({ ...DEFAULT_PREFERENCES, ...JSON.parse(raw) });
      }
    } catch {
      // Corrupt local data: fall back to defaults.
    }
  }, []);

  useEffect(() => {
    applyToDocument(preferences);
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(preferences));
    } catch {
      // Storage unavailable (private mode) — preferences stay in-memory.
    }
  }, [preferences]);

  const updatePreferences = useCallback((patch: Partial<UIPreferences>) => {
    setPreferences((current) => ({ ...current, ...patch }));
  }, []);

  const value = useMemo(
    () => ({ preferences, updatePreferences }),
    [preferences, updatePreferences],
  );

  return (
    <PreferencesContext.Provider value={value}>
      {children}
    </PreferencesContext.Provider>
  );
}

export function usePreferences(): PreferencesContextValue {
  const context = useContext(PreferencesContext);
  if (!context) {
    throw new Error("usePreferences must be used within PreferencesProvider");
  }
  return context;
}
